import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Flex,
  Label,
  LabeledInput,
  LabeledSelect,
  Text,
} from "@itwin/itwinui-react";
import { SvgAdd } from "@itwin/itwinui-icons-react";
import { IProducer } from "../../interfaces/IProducer";
import {
  ISubscriptionDraft,
  createEmptySubscriptionDraft,
} from "../../interfaces/IProvisioning";
import { EventSelector } from "../eventSelector/eventSelector";
import { DataCenterSelect } from "../dataCenterSelect/dataCenterSelect";
import { useGetDataCenters } from "../../hooks/useGetDataCenters/useGetDataCenters";

export interface SubscriptionDraftFormProps {
  producers: IProducer[];
  /** Existing draft to edit, or undefined for new */
  existingDraft?: ISubscriptionDraft;
  /** Called when user adds/saves the subscription draft */
  onSave: (draft: ISubscriptionDraft) => void;
  /** Called when user cancels editing (only shown when editing existing) */
  onCancel?: () => void;
  /** Whether this is in edit mode */
  isEditing?: boolean;
}

export const SubscriptionDraftForm = (props: SubscriptionDraftFormProps) => {
  const { producers, existingDraft, onSave, onCancel, isEditing = false } = props;

  const [draft, setDraft] = useState<ISubscriptionDraft>(
    existingDraft ?? createEmptySubscriptionDraft()
  );

  const [dataCentersResponse, dataCentersLoading, dataCentersError, getDataCenters] = useGetDataCenters();

  // Fetch data centers on mount
  useEffect(() => {
    getDataCenters();
  }, [getDataCenters]);

  // Reset form when existingDraft changes
  useEffect(() => {
    if (existingDraft) {
      setDraft(existingDraft);
    } else if (!isEditing) {
      setDraft(createEmptySubscriptionDraft());
    }
  }, [existingDraft, isEditing]);

  const producerOptions = useMemo(
    () =>
      producers.map((p) => ({
        label: p.name,
        value: p.id,
      })),
    [producers]
  );

  const statusOptions = [
    { value: "Active", label: "Active" },
    { value: "Disabled", label: "Disabled" },
  ];

  const selectedProducer = useMemo(
    () => producers.find((p) => p.id === draft.producerId),
    [producers, draft.producerId]
  );

  const handleProducerChange = useCallback((producerId: string) => {
    setDraft((prev) => ({
      ...prev,
      producerId,
      // Reset events when producer changes, default to all events
      events: [],
      useAllEvents: true,
    }));
  }, []);

  const handleEventsChange = useCallback(
    (events: string[], useAllEvents: boolean) => {
      setDraft((prev) => ({
        ...prev,
        events,
        useAllEvents,
      }));
    },
    []
  );

  const handleSave = useCallback(() => {
    // Prepare the draft for saving
    const finalDraft: ISubscriptionDraft = {
      ...draft,
      // If useAllEvents, ensure events array is empty for "no filter"
      events: draft.useAllEvents ? [] : draft.events,
    };
    onSave(finalDraft);

    // Reset form if not editing
    if (!isEditing) {
      setDraft(createEmptySubscriptionDraft());
    }
  }, [draft, onSave, isEditing]);

  // Validation
  const canSave = useMemo(() => {
    if (!draft.producerId) return false;
    // Must have events selected unless useAllEvents is true
    if (!draft.useAllEvents && draft.events.length === 0) return false;
    return true;
  }, [draft.producerId, draft.useAllEvents, draft.events.length]);

  const validationMessage = useMemo(() => {
    if (!draft.producerId) return "Select a producer";
    if (!draft.useAllEvents && draft.events.length === 0)
      return "Select at least one event or enable 'All events'";
    return null;
  }, [draft.producerId, draft.useAllEvents, draft.events.length]);

  return (
    <div style={{ padding: "8px 0" }}>
      <LabeledSelect
        label="Producer"
        options={producerOptions}
        value={draft.producerId}
        onChange={handleProducerChange}
        placeholder="Select a producer..."
        style={{ marginBottom: "12px" }}
      />

      {draft.producerId && (
        <>
          <Label style={{ marginBottom: "4px" }}>Events</Label>
          <EventSelector
            eventDefinitions={selectedProducer?.eventDefinitions ?? []}
            selectedEvents={draft.events}
            useAllEvents={draft.useAllEvents}
            onEventsChange={handleEventsChange}
          />
        </>
      )}

      {!draft.producerId && (
        <Text isMuted style={{ marginBottom: "12px" }}>
          Select a producer to configure events
        </Text>
      )}

      <LabeledSelect
        label="Status"
        options={statusOptions}
        value={draft.status}
        onChange={(value) => setDraft((prev) => ({ ...prev, status: value }))}
        style={{ marginBottom: "12px", marginTop: "12px" }}
      />

      <Text variant="small" style={{ marginBottom: "8px", marginTop: "16px" }}>
        Optional Filters
      </Text>
      <Flex flexDirection="row" flexWrap="wrap" gap="s">
        <Flex.Item flex="1 1 45%">
          <LabeledInput
            label="Account ID Filter"
            value={draft.accountIdFilter ?? ""}
            onChange={(e) =>
              setDraft((prev) => ({
                ...prev,
                accountIdFilter: e.target.value || undefined,
              }))
            }
            size="small"
          />
        </Flex.Item>
        <Flex.Item flex="1 1 45%">
          <LabeledInput
            label="iTwin ID Filter"
            value={draft.iTwinIdFilter ?? ""}
            onChange={(e) =>
              setDraft((prev) => ({
                ...prev,
                iTwinIdFilter: e.target.value || undefined,
              }))
            }
            size="small"
          />
        </Flex.Item>
        <Flex.Item flex="1 1 45%">
          <DataCenterSelect
            label="Resource Data Center ID"
            value={draft.resourceDataCenterIdFilter}
            onChange={(value) =>
              setDraft((prev) => ({
                ...prev,
                resourceDataCenterIdFilter: value,
              }))
            }
            dataCenters={dataCentersResponse?.dataCenters ?? []}
            isLoading={dataCentersLoading}
          />
        </Flex.Item>
        <Flex.Item flex="1 1 45%">
          <DataCenterSelect
            label="iTwin Data Center ID"
            value={draft.iTwinDataCenterIdFilter}
            onChange={(value) =>
              setDraft((prev) => ({
                ...prev,
                iTwinDataCenterIdFilter: value,
              }))
            }
            dataCenters={dataCentersResponse?.dataCenters ?? []}
            isLoading={dataCentersLoading}
          />
        </Flex.Item>
      </Flex>

      <Flex
        justifyContent="space-between"
        alignItems="center"
        style={{ marginTop: "16px" }}
      >
        <Flex gap="s">
          <Button
            onClick={handleSave}
            disabled={!canSave}
            startIcon={!isEditing ? <SvgAdd /> : undefined}
          >
            {isEditing ? "Save Changes" : "Add Subscription"}
          </Button>
          {isEditing && onCancel && (
            <Button styleType="default" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </Flex>
        {validationMessage && !canSave && (
          <Text isMuted style={{ fontSize: "0.85em" }}>
            {validationMessage}
          </Text>
        )}
      </Flex>
    </div>
  );
};
